import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { Button } from 'primeng/button';
import { AUTH_SESSION } from '@shared/di/auth-session.token';
import { RoleService } from '@shared/services/role.service';

/**
 * Shows the app only to the roles that can actually work in it, and explains the
 * situation to everyone else instead of leaving them in front of an interface
 * where nothing they click belongs to them.
 *
 * Same shape as {@link DesktopWrapperComponent}: it wraps the content and swaps
 * it for a full panel, rather than redirecting. No new route, no guard, and the
 * top bar stays reachable so logging out does not need a URL.
 *
 * **Who gets in:** `PRACTITIONER` and `SUPER_ADMIN`. This replaces the gate that
 * had been sitting commented out in the layout, which still named a
 * `PROFESSIONAL` role that no longer exists.
 *
 * ⚠️ `ADMIN` is now blocked. Several pages still read `roleService.isAdmin()` to
 * widen ownership checks (`isOwnerOrAdmin` in the program/session tables and
 * forms); those branches become unreachable from the web app. They are harmless
 * — an admin simply never gets there — but do not read them as evidence that an
 * admin can still use the platform.
 */
@Component({
  selector: 'app-role-gate',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Button],
  template: `
    @if (!resolved()) {
      <!-- Roles not loaded yet. Deliberately renders NOTHING rather than the
           block: the authority list starts empty, so treating "empty" as "not
           allowed" would flash the denial panel at every legitimate user during
           login. The layout owns the loading spinner. -->
    } @else if (allowed()) {
      <ng-content/>
    } @else {
      <div class="flex flex-col items-center justify-center text-center gap-6 px-6 py-16">
        <img [src]="image()" alt="" aria-hidden="true" class="h-auto w-64 max-w-full"/>

        <div class="flex flex-col gap-2 max-w-[34rem]">
          <h2 class="m-0 text-2xl font-semibold text-surface-900 dark:text-surface-0">{{ title() }}</h2>
          <p class="m-0 text-muted-color leading-normal">{{ description() }}</p>
        </div>

        <p-button i18n-label label="Esci" icon="pi pi-sign-out" severity="secondary"
                  [outlined]="true" (onClick)="logout()"/>
      </div>
    }
  `,
})
export class RoleGateComponent {
  /**
   * Illustration for the denial panel. An input so the placeholder can be
   * swapped for the designer's artwork without touching this component.
   */
  readonly image = input<string>('assets/images/doctors.svg');

  private readonly roleService = inject(RoleService);
  private readonly authService = inject(AUTH_SESSION);

  /**
   * True once the authorities are known. An account with no authority at all is
   * logged out by the layout, so an empty list here only ever means "still
   * loading".
   */
  protected readonly resolved = computed(() => this.roleService.authorityNames().length > 0);

  protected readonly allowed = this.roleService.canAccessPlatform;

  /**
   * A patient reaching the web app is the one case with somewhere else to go, so
   * it gets its own wording. Everything else is told plainly that the role has no
   * access, without inventing a destination we cannot promise.
   */
  protected readonly title = computed(() =>
    this.roleService.isMember()
      ? $localize`Questa piattaforma è riservata agli operatori`
      : $localize`Il tuo ruolo non ha accesso a questa piattaforma`,
  );

  protected readonly description = computed(() =>
    this.roleService.isMember()
      ? $localize`Il tuo profilo è quello di un paziente: le tue terapie e le tue sedute sono nell'app K·HERO. Da qui non c'è nulla che tu debba fare.`
      : $localize`Le funzionalità di questa piattaforma sono disponibili agli operatori clinici. Se pensi si tratti di un errore, contatta l'amministratore del tuo cliente.`,
  );

  protected logout(): void {
    return this.authService.logout(true, 'auth/login');
  }
}
